// src/hooks/useArticleMessages.ts
import { useCallback, useEffect, useState } from 'react';
import { ArticleService } from '@/libs/services/article';
import type { ArticleMessage } from '@/model/article';

type ChildrenMap = Record<string, ArticleMessage[]>;

export function useArticleMessages(table: string, tableId: string | number) {
  const [parents, setParents] = useState<ArticleMessage[]>([]);
  const [childrenMap, setChildrenMap] = useState<ChildrenMap>({});
  const [page, setPage] = useState(1);
  const [totalParents, setTotalParents] = useState<number | null>(null);

  const [loadingParents, setLoadingParents] = useState(false);
  const [loadingRepliesMap, setLoadingRepliesMap] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const hasMoreParents =
    typeof totalParents === 'number' && parents.length < totalParents ? true : false;

  const fetchParents = useCallback(
    async (targetPage: number, replace: boolean) => {
      if (!table || tableId === undefined || tableId === null || tableId === '') return;

      setLoadingParents(true);
      try {
        const { total, list } = await ArticleService.message({
          page: targetPage,
          table,
          table_id: tableId,
          is_parent: 1,
        });

        setTotalParents(total ?? null);
        setPage(targetPage);
        setParents((prev) => (replace ? list : [...prev, ...list]));
        setError(null);
      } catch (err) {
        console.error('[useArticleMessages] fetchParents error:', err);
        setError('留言載入失敗，請稍後再試');
      } finally {
        setLoadingParents(false);
      }
    },
    [table, tableId]
  );

  // 首次載入 / table or tableId 變動時重新載入
  useEffect(() => {
    setParents([]);
    setChildrenMap({});
    setTotalParents(null);
    setError(null);

    if (!table || tableId === undefined || tableId === null || tableId === '') return;
    void fetchParents(1, true);
  }, [table, tableId, fetchParents]);

  const loadMoreParents = useCallback(() => {
    if (loadingParents || !hasMoreParents) return;
    void fetchParents(page + 1, false);
  }, [loadingParents, hasMoreParents, fetchParents, page]);

  /** 共用：強制重抓某父留言的回覆列表 */
  const fetchReplies = useCallback(
    async (parentId: string) => {
      if (!parentId) return;
      if (!table || tableId === undefined || tableId === null || tableId === '') return;

      setLoadingRepliesMap((prev) => ({ ...prev, [parentId]: true }));

      try {
        const { list } = await ArticleService.message({
          page: 1,
          table,
          table_id: tableId,
          message_reply_id: Number(parentId),
        });

        setChildrenMap((prev) => ({
          ...prev,
          [parentId]: list,
        }));
      } catch (err) {
        console.error('[useArticleMessages] fetchReplies error:', err);
      } finally {
        setLoadingRepliesMap((prev) => {
          const next = { ...prev };
          delete next[parentId];
          return next;
        });
      }
    },
    [table, tableId]
  );

  /** 第一次展開用：若已載過就不重抓 */
  const loadRepliesFor = useCallback(
    async (parentId: string) => {
      if (!parentId) return;
      if (childrenMap[parentId] && childrenMap[parentId].length > 0) return;
      await fetchReplies(parentId);
    },
    [childrenMap, fetchReplies]
  );

  /** 送出 / 刪除後用：強制重抓該父留言整串回覆 */
  const reloadRepliesFor = useCallback(
    async (parentId: string) => {
      if (!parentId) return;
      await fetchReplies(parentId);
    },
    [fetchReplies]
  );

  /** 重新載入父留言列表（例如新增 / 刪除父留言後） */
  const reloadParents = useCallback(async () => {
    await fetchParents(1, true);
  }, [fetchParents]);

  /** 仍保留給未來有需要整體重整時使用 */
  const refreshAll = useCallback(async () => {
    setChildrenMap({});
    await fetchParents(1, true);
  }, [fetchParents]);

  return {
    parents,
    childrenMap,
    loadingParents,
    loadingRepliesMap,
    error,
    hasMoreParents,
    totalParents,
    loadMoreParents,
    loadRepliesFor,
    reloadRepliesFor,
    reloadParents,
    refreshAll,
  };
}
